import {
  Component,
  inject,
  OnDestroy,
  OnInit,
  ViewChild,
} from "@angular/core";
import { ActivatedRoute, Params, Router } from "@angular/router";
import { Subscription } from "rxjs";
import { ConnectionService } from "@services/connection-service";
import { BroadcastService } from "@services/broadcast-service";
import { EventLoggerService } from "@services/event-logger-service";
import { AppWebBridgeService } from "@services/app-web-bridge-service";
import { DataStoreService } from "@services/data-store-service";
import { CurrentComponentService } from "@services/current-component";
import { FilePickerComponent } from "@components/file-picker/file-picker.component";
import { LocalStorageService } from "@services/local-storage-service";
import { SwiperOptions } from "swiper";
import { AppConfig } from "../../../app.config";
import { InstantCheckupService } from "@services/instant-checkup-service";

@Component({
  selector: "app-checkup-photo-instruction",
  templateUrl: "./checkup-photo-instruction.component.html",
  styles: [":host {@apply tw-block tw-h-full tw-relative};"],
  standalone: false,
})
export class CheckupPhotoInstructionComponent implements OnInit, OnDestroy {
  private readonly conn: ConnectionService = inject(ConnectionService);
  private readonly router: Router = inject(Router);
  public readonly appConfig: AppConfig = inject(AppConfig);
  private readonly route: ActivatedRoute = inject(ActivatedRoute);
  private readonly eventLogger: EventLoggerService = inject(EventLoggerService);
  private readonly broadcast: BroadcastService = inject(BroadcastService);
  private readonly appWebBridgeService: AppWebBridgeService =
    inject(AppWebBridgeService);
  private readonly dataStore: DataStoreService = inject(DataStoreService);
  private readonly currentComponent: CurrentComponentService = inject(
    CurrentComponentService
  );
  private readonly localStorageService: LocalStorageService =
    inject(LocalStorageService);
  private readonly instantCheckupService: InstantCheckupService = inject(
    InstantCheckupService
  );

  user: any;
  tag: string;
  @ViewChild(FilePickerComponent, { static: false })
  filePickerComponent: FilePickerComponent;
  nativeCameraImage: any;
  subscriptions: Array<Subscription> = [];
  isFrontFacePhoto: boolean;
  isSideFacePhoto: boolean;
  isHairFrontPhoto: boolean;
  isHairTopPhoto: boolean;
  queryParams: { [key: string]: any };
  experiments: any;
  isCarouselReachedEnd: boolean = false;
  carouselConfig: SwiperOptions = {
    slidesPerView: 1,
    navigation: { prevEl: "nonext", nextEl: "nonext" },
    autoplay: {
      delay: 2000,
    },
    pagination: {
      type: "bullets",
    },
    on: {
      reachEnd: (): void => {
        this.isCarouselReachedEnd = true;
      },
    },
  };
  acqRebranding2025: boolean = false;

  constructor() {
    this.currentComponent.set(this);
  }

  async ngOnInit(): Promise<any> {
    const startTimer = performance.now();
    this.user = this.conn.getActingUser();
    this.appWebBridgeService.notifyWebLoginToken();
    this.subscriptions.push(
      this.route.queryParams.subscribe((params: any): void => {
        this.queryParams = params;
        this.tag =
          params.tag || this.appConfig.Shared.InstantCheckup.Type.FULL_FACE;
        this.tag = this.tag.toUpperCase();
      })
    );
    this.setInstantCheckupFlag();
    this.experiments = await this.conn.findUserActiveExperiments();
    this.experiments.forEach((experiment: any): void => {
      if (experiment.key === "acq_rebranding_2025") {
        this.acqRebranding2025 = true;
      }
    });
    const endTimer = performance.now();
    this.eventLogger.trackEvent("instant_photo_upload_screen", {
      timeInMillSec: endTimer - startTimer,
      userId: this.user.id,
    });
  }

  setInstantCheckupFlag(): void {
    switch (this.tag) {
      case this.appConfig.Shared.InstantCheckup.Type.HAIR_FRONT: {
        this.isHairFrontPhoto = true;
        break;
      }
      case this.appConfig.Shared.InstantCheckup.Type.HAIR_TOP: {
        this.isHairTopPhoto = true;
        break;
      }
      case this.appConfig.Shared.InstantCheckup.Type.SIDE_FACE:
      case this.appConfig.Shared.InstantCheckup.Type.RIGHT_SIDE_FACE:
      case this.appConfig.Shared.InstantCheckup.Type.LEFT_SIDE_FACE: {
        this.isSideFacePhoto = true;
        break;
      }
      case this.appConfig.Shared.InstantCheckup.Type.FULL_FACE:
      case this.appConfig.Shared.InstantCheckup.Type.FRONT_FACE: {
        this.isFrontFacePhoto = true;
        break;
      }
    }
  }

  takePhoto(): void {
    this.setInstantCheckupExtraData(this.route?.snapshot?.queryParams);
    const isReAcquisitionFlow = this.queryParams?.fromReacquisition === "true";
    if (this.queryParams?.tag && !isReAcquisitionFlow) {
      this.localStorageService.setValue(
        "expectedFacePhoto",
        this.queryParams.tag
      );
    }
    if (this.queryParams?.fromTakePhotoBanner) {
      this.localStorageService.setValue("takePhotoBannerClicked", true);
    }
    const handledByNative = this.instantCheckupService.startCapture(
      { ...this.queryParams },
      this.tag,
      this.queryParams?.fromTakePhotoBanner
    );
    if (!handledByNative) {
      this.filePickerComponent.openFileIntent();
    }
  }

  setInstantCheckupExtraData(queryParams: Params): void {
    this.localStorageService.setValue(
      "instantCheckupExtraData",
      JSON.stringify(queryParams)
    );
  }

  async uploadImageFromNativeCamera(event: any): Promise<any> {
    this.nativeCameraImage = event.target.files[0];
    this.dataStore.set("IMAGE_FILE", { file: this.nativeCameraImage });
    await this.router.navigate(["/user/instantCheckup/capture"], {
      queryParams: { ...this.queryParams, nativeCameraFile: true },
      replaceUrl: true,
    });
  }

  back(): void {
    this.broadcast.broadcast("NAVIGATION_BACK");
  }

  ngOnDestroy(): void {
    this.currentComponent.remove(this);
    this.subscriptions.forEach((subscription: Subscription): void =>
      subscription.unsubscribe()
    );
  }
}
